import { createContext, useState, useEffect, ReactNode, useCallback } from 'react'

import { DockConsentState, DockConsentChangedEvent, DockPrivacySettings } from '@/shared/types/dock'
import { i18n } from '@/utils/i18n'

export interface ConsentContextType {
    consentState: DockConsentState | null
    hasConvenienceConsent: boolean
    hasAnalysisConsent: boolean
    hasMarketingConsent: boolean
    hasUserRespondedToConsent: boolean
    isConsentInitialized: boolean
    openConsentSettings: () => void
    getConsentState: () => DockConsentState | null
}

export const CONSENT_STORAGE_KEY = 'mma_consent'

const ConsentContext = createContext<ConsentContextType>({
    consentState: null,
    hasConvenienceConsent: false,
    hasAnalysisConsent: false,
    hasMarketingConsent: false,
    hasUserRespondedToConsent: false,
    isConsentInitialized: false,
    openConsentSettings: () => {},
    getConsentState: () => null,
})

const ConsentProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [consentState, setConsentState] = useState<DockConsentState | null>(null)
    const [isConsentInitialized, setIsConsentInitialized] = useState(false)

    const mapLanguageToDockLocale = useCallback((language: string): string => {
        const languageMap: Record<string, string> = {
            'en': 'en-us',
            'de': 'de-de',
            'fr': 'fr-fr',
            'es': 'es-es',
            'cs': 'cs-cz',
            'hu': 'hu-hu',
            'ko': 'ko-kr',
            'nl': 'nl-nl',
            'pt': 'pt-pt',
            'ro': 'ro-ro',
            'zh': 'zh-cn'
        }

        return languageMap[language] || 'en-us'
    }, [])

    const updateDockLanguage = useCallback((language: string) => {
        const dockLocale = mapLanguageToDockLocale(language)

        document.documentElement.lang = language

        const dockElement = document.querySelector('dock-privacy-settings') as DockPrivacySettings | null
        if (dockElement) {
            dockElement.locale = dockLocale
        }
    }, [mapLanguageToDockLocale])

    const loadConsentFromStorage = useCallback((): DockConsentState | null => {
        try {
            const rawConsent = localStorage.getItem(CONSENT_STORAGE_KEY)
            if (rawConsent) {
                return JSON.parse(rawConsent) as DockConsentState
            }
        } catch (error) {
            console.error('Error loading consent from localStorage:', error)
        }
        return null
    }, [])

    const saveConsentToStorage = useCallback((consent: DockConsentState): void => {
        try {
            localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(consent))
        } catch (error) {
            console.error('Error saving consent to localStorage:', error)
        }
    }, [])

    const handleConsentChanged = useCallback((event: CustomEvent<DockConsentChangedEvent['detail']>) => {
        const newConsent = event.detail.consent
        setConsentState(newConsent)
        saveConsentToStorage(newConsent)
    }, [saveConsentToStorage])

    const openConsentSettings = useCallback((): void => {
        const dockElement = document.querySelector('dock-privacy-settings') as DockPrivacySettings | null
        if (dockElement) {
            dockElement.show()
        }
    }, [])

    const getConsentState = useCallback((): DockConsentState | null => {
        return consentState
    }, [consentState])

    useEffect(() => {
        const storedConsent = loadConsentFromStorage()
        if (storedConsent) {
            setConsentState(storedConsent)
        }
        setIsConsentInitialized(true)
        const eventListener = (event: Event) => {
            handleConsentChanged(event as CustomEvent<DockConsentChangedEvent['detail']>)
        }

        window.addEventListener('dock-consent-changed', eventListener)

        return () => {
            window.removeEventListener('dock-consent-changed', eventListener)
        }
    }, [loadConsentFromStorage, handleConsentChanged])

    useEffect(() => {
        const handleLanguageChange = (language: string) => {
            updateDockLanguage(language)
        }

        updateDockLanguage(i18n.resolvedLanguage || i18n.language || 'en')
        i18n.on('languageChanged', handleLanguageChange)

        return () => {
            i18n.off('languageChanged', handleLanguageChange)
        }
    }, [updateDockLanguage])

    const hasConvenienceConsent = consentState?.convenience === true
    const hasAnalysisConsent = consentState?.analysis === true
    const hasMarketingConsent = consentState?.marketing === true
    const hasUserRespondedToConsent = consentState !== null

    const contextValue: ConsentContextType = {
        consentState,
        hasConvenienceConsent,
        hasAnalysisConsent,
        hasMarketingConsent,
        hasUserRespondedToConsent,
        isConsentInitialized,
        openConsentSettings,
        getConsentState,
    }

    return (
        <ConsentContext.Provider value={contextValue}>
            {children}
        </ConsentContext.Provider>
    )
}

export { ConsentContext, ConsentProvider }